/**
 * FormSubmitPage — generic "+ Submit" page for any platform-form workflow.
 *
 * URL: /forms/:formId/submit
 *
 * Loads the form schema, renders FormRenderer, posts on submit, then
 * navigates the user to /workflow/items/:id (the existing item-detail
 * page) so they can see their submission in motion.
 *
 * Used by every module landing page's "+ Submit" button group (A4 wires
 * those buttons up). Also reachable directly via URL — handy for testing.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import FormRenderer from './FormRenderer';

export default function FormSubmitPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.getForm(formId)
      .then((r) => {
        if (cancelled) return;
        setForm(r.form);
        // Seed values with defaults
        const seeded = {};
        for (const section of r.form.schema.sections) {
          for (const field of section.fields) {
            if (field.default !== undefined) seeded[field.key] = field.default;
          }
        }
        setValues(seeded);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [formId]);

  const onSubmit = async (vals, fls) => {
    try {
      const r = await api.submitForm(form.id, vals, fls);
      // Navigate to the workflow item detail
      navigate(`/workflow/items/${r.workflow_item_id}`);
    } catch (e) {
      // Server-side per-field errors are returned as "key: reason"
      const msg = e.message || 'Submit failed';
      const m = msg.match(/^(\w+):\s*(.+)$/);
      if (m) setErrors({ [m[1]]: m[2] });
      throw e;
    }
  };

  if (loading) {
    return (
      <div className="page" style={{ padding: 24 }}>
        <div style={{ color: '#64748b' }}>Loading form…</div>
      </div>
    );
  }
  if (error || !form) {
    return (
      <div className="page" style={{ padding: 24 }}>
        <div style={{ color: '#dc2626', fontWeight: 600 }}>{error || 'Form not found'}</div>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: '28px 32px 48px', maxWidth: 1080, margin: '0 auto' }}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
          {form.schema.title || form.name}
        </h1>
        {form.description && (
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>{form.description}</p>
        )}
      </header>

      <FormRenderer
        schema={form.schema}
        values={values}
        onChange={setValues}
        files={files}
        onFilesChange={setFiles}
        errors={errors}
        submitLabel="Submit →"
        onSubmit={onSubmit}
      />
    </div>
  );
}
